import { useEffect, useRef, useState } from 'react';
interface Position { top: number; left: number }
const DRAG_THRESHOLD = 6;

export function useDraggableButton() {
    const ref = useRef<HTMLButtonElement | null>(null);
    const [pos, setPos] = useState<Position | null>(null);
    const posRef = useRef<Position | null>(null);
    const startPtr = useRef({ x: 0, y: 0 });
    const startPos = useRef<Position>({ top: 0, left: 0 });
    const isDragging = useRef(false);
    const didDrag = useRef(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const initId = requestAnimationFrame(() => {
            const rect = el.getBoundingClientRect();
            const initial: Position = {
                top: 0,
                left: window.innerWidth - rect.width,
            };
            posRef.current = initial;
            setPos(initial);
        });

        const onPointerDown = (e: PointerEvent) => {
            if (e.button !== 0) return;
            isDragging.current = true;
            didDrag.current = false;
            startPtr.current = { x: e.clientX, y: e.clientY };
            startPos.current = { ...posRef.current! };
            el.setPointerCapture(e.pointerId);
        };

        const onPointerMove = (e: PointerEvent) => {
            if (!isDragging.current || !posRef.current) return;

            const dx = e.clientX - startPtr.current.x;
            const dy = e.clientY - startPtr.current.y;
            if (!didDrag.current) {
                if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
                didDrag.current = true;
            }

            const rect = el.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const newTop = Math.min(startPos.current.top + dy, vh - rect.height);
            const newLeft = Math.min(startPos.current.left + dx, vw - rect.width);
            const next: Position = {
                top: newTop < 0 ? 0 : newTop,
                left: newLeft < 0 ? 0 : newLeft
            };
            posRef.current = next;
            setPos(next);
        };

        const onPointerUp = (e: PointerEvent) => {
            isDragging.current = false;
            try {
                el.releasePointerCapture(e.pointerId);
            } catch (e: unknown) {
                console.debug(`Pointer release failed: ${(e as Error).message}`);
            }
        };

        const onClickCapture = (e: MouseEvent) => {
            if (didDrag.current) {
                e.stopImmediatePropagation();
                didDrag.current = false;
            }
        };

        el.addEventListener('pointerdown', onPointerDown);
        el.addEventListener('click', onClickCapture, true);
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);

        return () => {
            cancelAnimationFrame(initId);
            el.removeEventListener('pointerdown', onPointerDown);
            el.removeEventListener('click', onClickCapture, true);
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
        };
    }, []);

    return { ref, pos };
}
